import { AlertTriangle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  confirmationQueueReducer,
  type ConfirmationOptions,
  type ConfirmationRequest,
} from './confirmation.js';

type Confirm = (options: ConfirmationOptions) => Promise<boolean>;

const ConfirmationContext = createContext<Confirm | null>(null);

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const nextId = useRef(1);
  const [requests, dispatch] = useReducer(confirmationQueueReducer, []);
  const requestsRef = useRef(requests);
  requestsRef.current = requests;

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        dispatch({
          type: 'enqueue',
          request: { ...options, id: nextId.current++, resolve },
        });
      }),
    [],
  );

  useEffect(
    () => () => {
      for (const request of requestsRef.current) request.resolve(false);
    },
    [],
  );

  const active = requests[0];
  const settle = useCallback((confirmed: boolean) => {
    const request = requestsRef.current[0];
    if (!request) return;
    request.resolve(confirmed);
    dispatch({ type: 'settle', id: request.id });
  }, []);

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      {active
        ? createPortal(
            <ConfirmationDialog key={active.id} request={active} onSettle={settle} />,
            document.body,
          )
        : null}
    </ConfirmationContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmationContext);
  if (!confirm) throw new Error('useConfirm must be used inside ConfirmationProvider');
  return confirm;
}

function ConfirmationDialog({
  request,
  onSettle,
}: {
  request: ConfirmationRequest;
  onSettle: (confirmed: boolean) => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef(document.activeElement as HTMLElement | null);
  const titleId = `confirmation-title-${request.id}`;
  const descriptionId = `confirmation-description-${request.id}`;

  useEffect(() => {
    const appRoot = document.getElementById('root');
    const rootWasInert = appRoot?.inert ?? false;
    const bodyOverflow = document.body.style.overflow;
    if (appRoot) appRoot.inert = true;
    document.body.style.overflow = 'hidden';
    cancelRef.current?.focus();
    return () => {
      if (appRoot) appRoot.inert = rootWasInert;
      document.body.style.overflow = bodyOverflow;
      if (previousFocus.current?.isConnected) previousFocus.current.focus();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end bg-black/45 sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onSettle(false);
      }}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onSettle(false);
            return;
          }
          if (event.key !== 'Tab') return;
          const buttons = [
            ...(panelRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []),
          ];
          if (buttons.length === 0) return;
          const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
          const next =
            current < 0
              ? event.shiftKey
                ? buttons.length - 1
                : 0
              : event.shiftKey
                ? (current - 1 + buttons.length) % buttons.length
                : (current + 1) % buttons.length;
          event.preventDefault();
          buttons[next]?.focus();
        }}
        className="max-h-[min(90dvh,40rem)] w-full overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl sm:max-w-md sm:rounded-2xl sm:pb-5"
      >
        <div className="flex items-start gap-3">
          <span className="shrink-0 rounded-full bg-danger/10 p-2 text-danger">
            <AlertTriangle className="size-5" />
          </span>
          <div className="min-w-0">
            <h2 id={titleId} className="break-words text-lg font-semibold">
              {request.title}
            </h2>
            <p
              id={descriptionId}
              className="mt-2 whitespace-pre-line break-words text-sm leading-relaxed text-ink-muted"
            >
              {request.description}
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onSettle(false)}
            className="min-h-11 rounded-full px-4 py-2 text-sm font-medium text-ink-muted hover:bg-hover"
          >
            {request.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => onSettle(true)}
            className="min-h-11 rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            {request.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
